import ListChecked20 from '@carbon/icons-react/es/list--checked/20';
import { useBoolean } from 'ahooks';
import { keyBy } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import { Switch } from 'components/atoms/switch/switch';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { SidebarCollapseButton } from 'components/molecules/sidebar-collapse-button/SidebarCollapseButton';
import { GroupFilterMenu } from 'components/organisms/filter-menu-sidebar/group-filter-menu';
import { useAllTasksKanbanGroupBy } from 'components/organisms/filter-menu-sidebar/use-group-by-hash';
import { AllTasksKanbanBoard } from 'components/organisms/kanban-board';
import {
  useColumnItems,
  useRowItems,
} from 'components/organisms/kanban-board/group-by';
import { TaskBoardGroupBy } from 'components/organisms/kanban-board/task-group-by';
import { FilterContainer } from 'components/organisms/tasks/components';
import {
  allTasksFiltersLocalStorageKey,
  excludedAllTasksFields,
} from 'components/organisms/tasks/constants';
import { GroupedFilterView } from 'components/organisms/tasks/grouped-filter-view';
import { useFiltersCaching } from 'components/organisms/tasks/use-filters-caching';
import { useGroupFilters } from 'components/organisms/tasks/use-group-filters';
import { AllTasksViewSwitcher } from 'components/pages/all-tasks/all-tasks-view-switcher';
import { GroupFieldsMeta } from 'components/pages/filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import { Argon } from 'components/templates';
import { TOGGLE_ALLTASKS_SIDEBAR } from 'constant/constant';
import { RootState } from 'data';
import { useDistractionFreeMode } from 'hooks/use-distraction-free-mode';
import { useTranslation } from 'hooks/use-translation';
import { useQueryJiraConfig } from 'queries/jira-integration';
import { FieldsStore, TaskField } from 'stores/fields';
import { useAllTasksRefresh } from './use-all-tasks-refresh';
import { useAllTasksSocketUpdates } from './use-all-tasks-socket-updates';
import { useFieldsAsGroupedFields } from './use-fields-as-grouped-fields';

const FlexWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: var(--spacing-03);
  width: 150px;
`;

const StyledToggle = styled(Switch)`
  margin-right: var(--spacing-03);
`;

export const AllTasksBoardPage: FC = () => {
  const title = useTranslation('all_tasks.title');
  const buttonTitle = useTranslation('hide_empty_rows');
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const fields_loading = !FieldsStore.loaded(fieldsStore);
  const { data: jiraIntegrationConfig } = useQueryJiraConfig();

  const excludedFields = useMemo(() => {
    // remove jiraIntegration from excluded fields ONLY when the integration is active
    if (jiraIntegrationConfig?.length) {
      return excludedAllTasksFields.filter(
        (field) => field !== 'jiraIntegration',
      );
    }

    return excludedAllTasksFields;
  }, [jiraIntegrationConfig]);

  const excludedFieldsForColumns = useMemo(
    () => [...excludedFields, 'private', 'completedLateBy', 'phaseName'],
    [excludedFields],
  );

  const excludedFieldsForFilters = useMemo(
    () => [...excludedFields, 'projectPhaseId'],
    [excludedFields],
  );

  const fields = useMemo(
    () =>
      FieldsStore.enabledTaskFields(fieldsStore).filter(
        ({ fieldName }) => !excludedAllTasksFields.includes(fieldName),
      ),
    [fieldsStore],
  );

  const fieldsForFilter = useMemo(
    () =>
      FieldsStore.enabledTaskFields(fieldsStore).filter(
        ({ fieldName }) => !excludedFieldsForFilters.includes(fieldName),
      ),
    [fieldsStore, excludedFieldsForFilters],
  );

  const fields_lookup_by_name = useMemo(
    () =>
      keyBy(
        fieldsForFilter.filter(
          ({ fieldName }) => !excludedFieldsForColumns.includes(fieldName),
        ),
        'fieldName',
      ) as Record<string, TaskField>,
    [excludedFieldsForColumns, fieldsForFilter],
  );

  const { byProjectId, projectIds, ids, byId } = useSelector(
    ({ tasks_v2 }: RootState) => tasks_v2,
    shallowEqual,
  );
  const loaded_projects_count = projectIds.filter(
    (p) => byProjectId[p][0] === 'loaded',
  ).length;
  const total_projects_count = projectIds.length;
  const groupLookup: GroupFieldsMeta = useMemo(() => {
    return [
      {
        id: FilterGroupType.Task,
        fields: fields_lookup_by_name,
      },
    ];
  }, [fields_lookup_by_name]);
  const { filter, filterFunction, clearFilters, selectFilter } =
    useGroupFilters(groupLookup, FilterGroupType.Task, !fields_loading);
  const tasks_loading = ids[0] === 'loading';

  /**
   * Commenting the progress bar since it blocks main thread and gives app frozen experience.
   * Adding a temporary local state to pass progress prop.
   * TODO: to analyse the progress bar.
   */
  // const { progress, start, complete } = useProgress(100);
  const [progress, setProgress] = useState(0);
  const start = useCallback(() => setProgress(0), [setProgress]);
  const complete = useCallback(() => setProgress(100), [setProgress]);

  useAllTasksRefresh(start, complete);
  useAllTasksSocketUpdates();
  useFiltersCaching({
    localStorageKey: allTasksFiltersLocalStorageKey,
  });
  const { groupedFields } = useFieldsAsGroupedFields({
    taskFields: fieldsForFilter,
  });

  const [groupBy, setGroupBy] = useAllTasksKanbanGroupBy(
    fields_lookup_by_name,
    fields_loading,
  );

  const rowFields = useRowItems(fields, true);
  const columnFields = useColumnItems(fields);
  const projectIdsArr = useMemo(() => {
    return projectIds.map((id) => ProjectId(id));
  }, [projectIds]);

  const [isGroupChangeLoading, { setTrue, setFalse }] = useBoolean(false);
  const [collapse, { toggle }] = useBoolean(false);

  const setGroupChangeLoading = (value: boolean) => {
    return value ? setTrue() : setFalse();
  };

  const isLoading = useMemo(() => {
    return (
      isGroupChangeLoading ||
      fields_loading ||
      tasks_loading ||
      total_projects_count !== loaded_projects_count
    );
  }, [
    isGroupChangeLoading,
    fields_loading,
    tasks_loading,
    total_projects_count,
    loaded_projects_count,
  ]);
  const { active, exit, enter } = useDistractionFreeMode(
    TOGGLE_ALLTASKS_SIDEBAR,
  );

  function sidebarToggle() {
    if (active) {
      exit();
    } else {
      enter();
    }
  }

  return (
    <Argon
      isDistractionFree={active}
      title={<HeaderWithIconTitle icon={<ListChecked20 />} title={title} />}
      sider={
        <GroupFilterMenu
          lookup={groupLookup}
          taskLookup={fields_lookup_by_name}
          filter={filter}
          fields={fields}
          selectFilter={selectFilter}
          clearFilters={clearFilters}
          isFieldsLoading={fields_loading}
          setKanbanBoardGroupChangeLoading={setGroupChangeLoading}
        />
      }
      progress={progress}
      actions={{
        primary: (
          <FilterContainer>
            <SidebarCollapseButton sidebarToggle={sidebarToggle} />
            <GroupedFilterView
              isFieldsLoading={fields_loading}
              filter={filter}
              fields={groupedFields}
              selectFilter={selectFilter}
              clearFilters={clearFilters}
              lookup={groupLookup}
              taskLookup={fields_lookup_by_name}
            />
          </FilterContainer>
        ),
        secondary: (
          <>
            <FlexWrapper>
              <StyledToggle onChange={toggle} size="small" />
              <span>{buttonTitle}</span>
            </FlexWrapper>

            <TaskBoardGroupBy
              isLoading={isLoading}
              groupBy={groupBy}
              setGroupBy={setGroupBy}
              columnFields={columnFields}
              rowFields={rowFields}
              setGroupChangeLoading={setGroupChangeLoading}
            />
          </>
        ),
        central: <AllTasksViewSwitcher />,
      }}
      content={
        <AllTasksKanbanBoard
          fields={fields}
          filterFunction={filterFunction}
          ids={ids[1] ?? []}
          loading={isLoading}
          lookup={byId}
          groupBy={groupBy}
          projectIds={projectIdsArr}
          isColumnHidden={collapse}
        />
      }
    />
  );
};
